import type { Primitive } from '@/core/types/mutable-types';
import { objectKeys } from '@/core/utils/object';

const isDate = (d: unknown): d is Date => d instanceof Date;
const isSet = (d: unknown): d is Set<unknown> => d instanceof Set;
const isMap = (d: unknown): d is Map<unknown, unknown> => d instanceof Map;
const isObject = (o: unknown): o is Record<string, unknown> => o != null && typeof o === 'object';

export type Delta<T> = T extends Primitive
  ? T
  : T extends Date
    ? T
    : T extends Array<infer U>
      ? Record<number, Delta<U>>
      : T extends Set<infer U>
        ? Record<number, Delta<U>>
        : T extends Map<infer K, infer V>
          ? Record<number, [K, Delta<V>]>
          : {
              [P in keyof T]?: Delta<T[P]>;
            };

/**
 * Recursively returns the delta between two objects.
 * If there is no difference, it returns undefined.
 * **Note:** This function can also return undefined if the target is undefined.
 *
 * Sets and maps are treated as arrays. For arrays, it calculates the delta between
 * source and target elements at the same index. Missing elements in the target
 * will be reported as undefined. The delta for arrays is represented as an object
 * with indexes as keys.
 */
export function getDelta<T>(source: T, target: T): Delta<T> | undefined {
  // if source and target are the same, return undefined
  if (source === target) {
    return undefined;
  }

  // primitive types, we know they are different so return target
  if (!isObject(target) || target === null || target === undefined) {
    return target as Delta<T> | undefined;
  }

  // process arrays
  if (Array.isArray(target) && Array.isArray(source)) {
    const result: Record<number, unknown> = {};
    let hasDiff = false;

    for (let i = 0; i < Math.max(source.length, target.length); i++) {
      // if source has more elements than target, report them as undefined
      if (target[i] === undefined && source[i] !== undefined) {
        result[i] = undefined;
        hasDiff = true;
        continue;
      }

      // calculate delta for elements at the same index
      const delta = getDelta(source[i], target[i]);

      // report only if there is a difference
      if (delta !== undefined) {
        result[i] = delta;
        hasDiff = true;
      }
    }

    if (!hasDiff) {
      return undefined;
    }

    return result as Delta<T>;
  }

  // process dates, return target if they are different
  if (isDate(target) && isDate(source)) {
    const targetValue = target.valueOf();
    const sourceValue = source.valueOf();

    if (targetValue === sourceValue) {
      return undefined;
    }

    return target as Delta<T>;
  }

  // treat sets as arrays
  if (isSet(target) && isSet(source)) {
    return getDelta([...source].sort(), [...target].sort()) as Delta<T> | undefined;
  }

  // treat maps as arrays
  if (isMap(target) && isMap(source)) {
    return getDelta([...source].sort(), [...target].sort()) as Delta<T> | undefined;
  }

  // here we handled other supported types, so we can process target as an object
  if (isObject(target) && isObject(source)) {
    const result: Record<string, unknown> = {};
    let hasDiff = false;

    for (const key of objectKeys(target)) {
      // target value can be undefined, so we need to check it explicitly
      // to distinguish between undefined value and non-changed value
      if (target[key] === undefined && source[key] !== undefined) {
        result[key] = undefined;
        hasDiff = true;
        continue;
      }

      const delta = getDelta(source[key], target[key]);

      if (delta !== undefined) {
        result[key] = delta;
        hasDiff = true;
      }
    }

    // we want to report keys that are in source but not in target
    // so we need to iterate over source keys as well
    for (const key of objectKeys(source)) {
      // skip keys that are already processed
      if (key in target) {
        continue;
      }

      // source value can be undefined, so we need to check it explicitly
      if (target[key] === undefined && source[key] !== undefined) {
        result[key] = undefined;
        hasDiff = true;
        continue;
      }

      const delta = getDelta(source[key], target[key]);

      if (delta !== undefined) {
        result[key] = delta;
        hasDiff = true;
      }
    }

    // if there is no difference, return undefined
    if (!hasDiff) {
      return undefined;
    }

    return result as Delta<T> | undefined;
  }

  // if source and target have different types they cannot be compared,
  // just return target
  return target as Delta<T>;
}

/**
 * Returns true if source and target objects are deeply equal.
 */
export function deepEquals<T>(source: T, target: T): boolean {
  if (target === undefined && source !== undefined) {
    return false;
  }

  return getDelta(source, target) === undefined;
}

/**
 * Compares two objects with potentially different types.
 * Returns true if they are structurally equivalent.
 * Handles type coercion for primitive types.
 */
export function compareObjects(source: unknown, target: unknown): boolean {
  // Handle null and undefined cases
  if (source === null && target === null) return true;
  if (source === undefined && target === undefined) return true;
  if (source === null || target === null) return false;
  if (source === undefined || target === undefined) return false;

  // Handle primitive types with type coercion
  if (typeof source !== 'object' && typeof target !== 'object') {
    // Convert to same type for comparison
    if (typeof source === 'string' && typeof target === 'number') {
      return !isNaN(Number(source)) && Number(source) === target;
    }
    if (typeof source === 'number' && typeof target === 'string') {
      return !isNaN(Number(target)) && source === Number(target);
    }
    if (typeof source === 'boolean' && typeof target === 'string') {
      return source === (target === 'true' || target === '1');
    }
    if (typeof source === 'string' && typeof target === 'boolean') {
      return (source === 'true' || source === '1') === target;
    }
    if (typeof source === 'boolean' && typeof target === 'number') {
      return source === (target === 1);
    }
    if (typeof source === 'number' && typeof target === 'boolean') {
      return (source === 1) === target;
    }
    return source === target;
  }

  // Handle Date objects
  if (source instanceof Date && target instanceof Date) {
    return source.getTime() === target.getTime();
  }
  if (source instanceof Date || target instanceof Date) {
    return false;
  }

  // Handle Array objects
  if (Array.isArray(source) && Array.isArray(target)) {
    if (source.length !== target.length) return false;
    for (let i = 0; i < source.length; i++) {
      if (!compareObjects(source[i], target[i])) return false;
    }
    return true;
  }
  if (Array.isArray(source) || Array.isArray(target)) {
    return false;
  }

  // Handle Set objects
  if (source instanceof Set && target instanceof Set) {
    if (source.size !== target.size) return false;
    const sourceArray = Array.from(source).sort();
    const targetArray = Array.from(target).sort();
    return compareObjects(sourceArray, targetArray);
  }
  if (source instanceof Set || target instanceof Set) {
    return false;
  }

  // Handle Map objects
  if (source instanceof Map && target instanceof Map) {
    if (source.size !== target.size) return false;
    const sourceArray = Array.from(source.entries()).sort();
    const targetArray = Array.from(target.entries()).sort();
    return compareObjects(sourceArray, targetArray);
  }
  if (source instanceof Map || target instanceof Map) {
    return false;
  }

  // Handle regular objects
  if (typeof source === 'object' && typeof target === 'object') {
    const sourceKeys = Object.keys(source as Record<string, unknown>);
    const targetKeys = Object.keys(target as Record<string, unknown>);

    if (sourceKeys.length !== targetKeys.length) return false;

    for (const key of sourceKeys) {
      if (!targetKeys.includes(key)) return false;

      const sourceValue = (source as Record<string, unknown>)[key];
      const targetValue = (target as Record<string, unknown>)[key];

      if (!compareObjects(sourceValue, targetValue)) return false;
    }

    return true;
  }

  // If we reach here, types are incompatible
  return false;
}
